// Shared Constants: Order Sources
// This file is the single source of truth for order sources.
// It is copied to frontend/src/shared/ and backend/src/shared/ by sync-shared.js
//
// Faz 3: PLATFORM_MAGAZA format standardization (Karar 1)
//
// SAAS-COUPLING: aşağıdaki yedi değer TrioCare'in mağaza kimliğidir (PROBED, LaborTech,
// TrioCare × Shopify/Trendyol/Hepsiburada) — hardcoded — extraction'da kanal tablosuna
// taşınır: tenant kendi mağazalarını tanımlar, kod listeyi koddan değil veriden okur.
// Bugün mağaza eklemek ÜÇ yeri birden değiştirmeyi gerektiriyor: bu dosya, `siparis_kaynak`
// DB enum'u (migration) ve mağazayı kimlik bilgisine bağlayan config (shopify/trendyol/
// hepsiburada) — üçü ayrışırsa sipariş ya yazılamaz ya yanlış mağazaya yazılır.
// `LEGACY_ALIASES` ayrıca TrioCare'in kendi geçmişi (eski tek-mağazalı PROBED/LABORTECH
// değerleri); yeni bir tenant'ta karşılığı yoktur, extraction'da boş başlar.

export const ORDER_SOURCES = {
    SHOPIFY_PROBED: 'SHOPIFY_PROBED',
    SHOPIFY_LABORTECH: 'SHOPIFY_LABORTECH',
    HARICI: 'HARICI',
    TRENDYOL_LABORTECH: 'TRENDYOL_LABORTECH',
    HEPSIBURADA_LABORTECH: 'HEPSIBURADA_LABORTECH',
    TRENDYOL_TRIOCARE: 'TRENDYOL_TRIOCARE',
    HEPSIBURADA_TRIOCARE: 'HEPSIBURADA_TRIOCARE'
} as const;

// Platform-based groups
export const SHOPIFY_SOURCES = [ORDER_SOURCES.SHOPIFY_PROBED, ORDER_SOURCES.SHOPIFY_LABORTECH] as const;
export const TRENDYOL_SOURCES = [ORDER_SOURCES.TRENDYOL_LABORTECH, ORDER_SOURCES.TRENDYOL_TRIOCARE] as const;
export const HEPSIBURADA_SOURCES = [ORDER_SOURCES.HEPSIBURADA_LABORTECH, ORDER_SOURCES.HEPSIBURADA_TRIOCARE] as const;
export const MARKETPLACE_SOURCES = [...TRENDYOL_SOURCES, ...HEPSIBURADA_SOURCES] as const;
export const ALL_SOURCES = Object.values(ORDER_SOURCES);

// Kaynakların ekranda görünen adı. Ön yüz rozetleri ve listeleri (`kaynakLabel`), arka yüz
// üretim listesi Excel'i ve günlük e-posta raporu (`formatHelper.kaynakEtiketi`) aynı
// tablodan okur; ad yalnız burada yazılır. Sistem kodu (anahtar) değişmez, yalnız ad.
//
// SAAS-COUPLING: mağaza adları TrioCare'in mağazalarına özel — hardcoded — extraction'da
// kanal tablosuna taşınır: tenant mağazasının adını kendi tanımlar.
export const SOURCE_LABELS: Record<OrderSource, string> = {
    [ORDER_SOURCES.SHOPIFY_PROBED]: 'PROBED',
    [ORDER_SOURCES.SHOPIFY_LABORTECH]: 'KANAL2',
    [ORDER_SOURCES.HARICI]: 'HARICI',
    [ORDER_SOURCES.TRENDYOL_LABORTECH]: 'TY-Kanal2',
    [ORDER_SOURCES.TRENDYOL_TRIOCARE]: 'TY-TrioCare',
    [ORDER_SOURCES.HEPSIBURADA_LABORTECH]: 'HB-Kanal2',
    [ORDER_SOURCES.HEPSIBURADA_TRIOCARE]: 'HB-TrioCare'
};

// Online channels: every source EXCEPT HARICI, i.e. orders that arrive from an
// integration instead of being typed in by hand. Report filters ("online only")
// use this set. Composed rather than filtered so that adding a new platform group
// is a visible edit here; `kaynakListeleri.test.js` pins it to ALL_SOURCES - HARICI.
//
// NAME CLASH WARNING (rota 1.17): the frontend helper `isOtomatikKaynak` means
// Shopify + Trendyol today and does NOT include Hepsiburada (rota 1.23). This set
// does. Same idea, different coverage - do not swap one for the other.
export const ONLINE_SOURCES = [...SHOPIFY_SOURCES, ...MARKETPLACE_SOURCES] as const;

// SQL IN(...) bodies, so report and model queries never spell a source list out
// in plain text (rota 1.18). Usage:
//   `AND kaynak IN (${SQL_TRENDYOL_SOURCES})`
// Values are enum literals produced from the constants above, never user input.
export const SQL_SHOPIFY_SOURCES = SHOPIFY_SOURCES.map((v) => `'${v}'`).join(', ');
export const SQL_TRENDYOL_SOURCES = TRENDYOL_SOURCES.map((v) => `'${v}'`).join(', ');
export const SQL_HEPSIBURADA_SOURCES = HEPSIBURADA_SOURCES.map((v) => `'${v}'`).join(', ');

// Legacy aliases — old DB value → new DB value
// Used during migration transition and backward compatibility checks
export const LEGACY_ALIASES: Record<string, string> = {
    PROBED: ORDER_SOURCES.SHOPIFY_PROBED,
    LABORTECH: ORDER_SOURCES.SHOPIFY_LABORTECH,
    TRENDYOL: ORDER_SOURCES.TRENDYOL_LABORTECH,
    HEPSIBURADA: ORDER_SOURCES.HEPSIBURADA_LABORTECH
};

// Type exports for TypeScript consumers
export type OrderSource = typeof ORDER_SOURCES[keyof typeof ORDER_SOURCES];
